import { getPrice } from "@/lib/strapi";
import { ArrowRight, Phone } from "lucide-react";
import Link from "next/link";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

interface ClientProps {
  product: any;
}

export function ProductCard({ product }: ClientProps) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col"
    >
      {/* Product Image */}
      <div className="relative overflow-hidden h-[260px]">
        <img
          src={product?.image?.medium}
          alt={product.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3">
          <Badge className="bg-primary text-white text-xs px-3 py-1 hover:bg-primary/90 shadow-sm">
            {product.category}
          </Badge>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-lg font-bold text-foreground leading-snug">
            {product.name}
          </h3>
        </div>

        {/* Description */}
        <p className="text-muted-foreground text-sm leading-relaxed mb-4 line-clamp-2">
          {product.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.tags.slice(0, 3).map((tag: string) => (
            <span
              key={`product-${product.id}-tags-${tag}`}
              className="text-xs font-medium px-2.5 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Features List */}
        <div className="mb-4 flex-grow">
          <p className="text-slate-600 text-xs font-bold mb-2.5 uppercase tracking-wider">
            Tính Năng
          </p>
          <ul className="space-y-1.5">
            {product.features.slice(0, 5).map((feature: string) => (
              <li
                key={`product-${product.id}-features-${feature}`}
                className="flex items-start gap-2"
              >
                <span className="text-primary font-bold text-xs leading-5 flex-shrink-0">
                  •
                </span>
                <span className="text-slate-600 text-sm leading-5">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-100 my-4" />

        {/* Price Section */}
        <div className="mb-4">
          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">
            Giá
          </p>
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-xl font-bold text-primary">
              { product.price == "0" ? "Liên hệ" : getPrice(product.price) }
            </span>
            {product.price !== "0" && (
              <span className="text-muted-foreground text-sm font-medium">
                {product.pricePeriod}
              </span>
            )}
          </div>
          <p className="text-slate-400 text-xs mt-1">{product.priceNote}</p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-2 mt-auto">
          <Link href={`/san-pham/${product.slug}`}>
            <Button className="w-full bg-primary text-white hover:bg-primary/90 flex items-center justify-center gap-2 text-sm font-semibold h-10">
              <ArrowRight className="h-4 w-4" /> Xem Chi Tiết
            </Button>
          </Link>
          <Link href="#lien-he">
            <Button
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary/90 text-sm font-semibold h-10"
            >
              <Phone className="h-4 w-4" /> Liên Hệ Tư Vấn
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
